import React, { memo, useState } from 'react';
import { format } from 'date-fns';

import { areEqual } from 'Utils/equalityChecks';
import { AssetPlacement } from 'Containers/SerializedEquipment/types';

import classes from './assetPlacementList.module.css';

interface Props {
  placements: AssetPlacement[];
  // When provided, each row gets an "Edit" control to correct its dates. Save is called with only
  // the changed dates (date_out is only editable on a closed placement — end an open one via
  // check-out). savingId disables the row being saved.
  onSaveDates?: (placement: AssetPlacement, payload: { date_in?: string; date_out?: string }) => void;
  savingId?: number | null;
  // When provided, closed (historical) rows get a Delete control with an inline confirm. The active
  // placement is never deletable (end it via check-out). deletingId disables the row being deleted.
  onDeletePlacement?: (placement: AssetPlacement) => void;
  deletingId?: number | null;
}

function fmt(value: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'MMM d, yyyy');
}

// ISO datetime -> Y-m-d for a <input type="date">.
const toDateInput = (iso?: string | null): string => (iso ? iso.slice(0, 10) : '');

function roomLabel(placement: AssetPlacement): string {
  return placement.room?.room_type?.name || `Room ${placement.room_id}`;
}

function projectLabel(placement: AssetPlacement): string {
  return placement.project?.alias || placement.project?.uid || `Project ${placement.project_id}`;
}

function AssetPlacementList({ placements, onSaveDates, savingId, onDeletePlacement, deletingId }: Props) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [dateIn, setDateIn] = useState('');
  const [dateOut, setDateOut] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null);

  const startEdit = (placement: AssetPlacement) => {
    setEditingId(placement.id);
    setDateIn(toDateInput(placement.date_in));
    setDateOut(toDateInput(placement.date_out));
  };

  const save = (placement: AssetPlacement) => {
    if (onSaveDates) {
      // date_out is only editable for a closed placement; end an open one via check-out.
      onSaveDates(placement, placement.is_open ? { date_in: dateIn } : { date_in: dateIn, date_out: dateOut });
    }
    setEditingId(null); // close optimistically; the container reloads and surfaces any error
  };

  if (placements.length === 0) {
    return (
      <p className={classes.empty} data-testid="history-empty">
        No placements recorded yet.
      </p>
    );
  }

  return (
    <ul className={classes.list} data-testid="asset-placement-list">
      {placements.map((placement) => {
        const range = `${fmt(placement.date_in)} — ${placement.date_out ? fmt(placement.date_out) : 'Present'}`;
        const busy = savingId === placement.id || deletingId === placement.id;
        return (
          <li key={placement.id} className={classes.item} data-testid="history-row">
            <div className={classes.itemHeader}>
              <span className={classes.location}>{roomLabel(placement)}</span>
              <span className={classes.project}>{`(${projectLabel(placement)})`}</span>
              {placement.is_open && <span className={classes.currentBadge}>Current</span>}
              {editingId !== placement.id && (
                <span className="ms-auto d-flex align-items-center gap-2">
                  {confirmingDeleteId === placement.id ? (
                    <>
                      <span className="small text-muted">Delete record?</span>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger"
                        disabled={busy}
                        onClick={() => {
                          if (onDeletePlacement) onDeletePlacement(placement);
                          setConfirmingDeleteId(null);
                        }}
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-muted"
                        onClick={() => setConfirmingDeleteId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      {onSaveDates && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          disabled={busy}
                          onClick={() => startEdit(placement)}
                        >
                          Edit
                        </button>
                      )}
                      {onDeletePlacement && !placement.is_open && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-danger"
                          disabled={busy}
                          onClick={() => setConfirmingDeleteId(placement.id)}
                        >
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </span>
              )}
            </div>

            {editingId === placement.id ? (
              <div className="d-flex flex-wrap align-items-end gap-2 mt-1" data-testid="history-edit">
                <div>
                  <label className="form-label mb-0 small" htmlFor={`date-in-${placement.id}`}>
                    Deploy date
                  </label>
                  <input
                    id={`date-in-${placement.id}`}
                    type="date"
                    className="form-control form-control-sm"
                    value={dateIn}
                    onChange={(e) => setDateIn(e.target.value)}
                    data-testid="history-date-in"
                  />
                </div>
                {!placement.is_open && (
                  <div>
                    <label className="form-label mb-0 small" htmlFor={`date-out-${placement.id}`}>
                      End date
                    </label>
                    <input
                      id={`date-out-${placement.id}`}
                      type="date"
                      className="form-control form-control-sm"
                      value={dateOut}
                      onChange={(e) => setDateOut(e.target.value)}
                      data-testid="history-date-out"
                    />
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  disabled={busy || !dateIn}
                  onClick={() => save(placement)}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-muted"
                  onClick={() => setEditingId(null)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className={classes.dates}>{range}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

AssetPlacementList.defaultProps = {
  onSaveDates: undefined,
  savingId: null,
  onDeletePlacement: undefined,
  deletingId: null,
};

const AssetPlacementListMemo = memo(AssetPlacementList, areEqual);

export { AssetPlacementListMemo as AssetPlacementList };
